import Box from '@mui/material/Box';
import {
  SyntheticEvent, useCallback, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import Stack from '@mui/material/Stack';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';
import { FormProvider, useForm } from 'react-hook-form';
import moment from 'moment-timezone';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { BaseTooltip, BaseButtonMenu } from '@mgm/common/components/base';
import { OrdersList } from './Orders';
import { OrdersTotalsHeader } from './Orders/components/OrdersTotalHeader';
import {
  BaseTabs, MobileTabs,
} from '../../components/base';
import { CommissionsTab } from './Commissions';
import { useCommissionedSalesPermissions } from './hooks/useCommissionedSalesPermissions';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import {
  getAttributedOrders,
  getDorChosenDate,
  getIsOrdersLoading,
  getOrdersChosenDates,
} from '../../store/orders/selectors';
import { summarizeByDayAndLocation, summarizeByLocation } from '../../utils/helpers/commission.helper';
import { useDownloadReportCSV } from './hooks/useReports';
import { TListBaseItem } from '../../components/base/BList/type';
import { usePermissions } from '../../hooks/useCommon';
import { OrdersTour } from './Shared/components';
import { TeamsSummary } from './Teams';
import { useFeature } from '../../hooks/useFeature';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { PromotersSummary } from './Promoters';
import { getIsOpenShopifyIdVideo } from '../../store/team/selectors';
import { VideoModal } from '../../components';
import { memberActions } from '../../store/team/slice';

const Orders = () => {
  const { t } = useTranslation(['orders', 'commissions', 'timesheets', 'payroll', 'common', 'settings']);
  const { commissionedSalesPermissions } = useCommissionedSalesPermissions();
  const { shop } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();

  const { downloadReportCSV, downloadDailyReportCSV } = useDownloadReportCSV();
  const { permissions } = usePermissions();
  const [runTour, setRunTour] = useState(false);
  const dorChosenDate = useAppSelector(getDorChosenDate);
  const { allowFeatureAccess } = useFeature();

  const [tab, setTab] = useState('1');
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);
  const attributedOrders = useAppSelector(getAttributedOrders);
  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const isOpenShopifyIdVideo = useAppSelector(getIsOpenShopifyIdVideo);

  const exportReport = useCallback(() => {
    const summary = summarizeByLocation(attributedOrders);
    downloadReportCSV(summary);
  }, [attributedOrders]);

  const exportDaily = () => {
    const daily = summarizeByDayAndLocation(attributedOrders, shop?.shopTimezone);
    downloadDailyReportCSV(daily);
  };

  const handleStartTour = () => {
    setRunTour(true);
    setColumnVisibilityModel({
      id: false,
      processedAt: false,
      locationId: false,
    });
  };

  const handleFinishTour = () => {
    setRunTour(false);
    setColumnVisibilityModel({});
  };

  const items: Array<TListBaseItem> = [
    {
      name: t('dailyReport', { ns: 'orders' }),
      value: 'daily',
      isSelected: false,
      action: () => {
        exportDaily();
      },
    },
    {
      name: t('summaryReport', { ns: 'orders' }),
      value: 'summary',
      isSelected: false,
      action: () => {
        exportReport();
      },
    },
  ];

  const tabs = [
    {
      label: t('orders', { ns: 'timesheets' }),
      value: '1',
    },
    {
      label: t('individualSummary'),
      value: '2',
    },
    allowFeatureAccess('teamCommissions') && {
      label: t('teamsSummary'),
      value: '3',
    },
    {
      label: t('promotersSummary'),
      value: '4',
    },
  ].filter(Boolean) as Array<{ label: string, value: string }>;

  const handleChange = (_event: SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  const methods = useForm({
    mode: 'all',
    values: {
      dateRange: ordersChosenDates,
      date: moment(dorChosenDate),
    },
  });

  const TabsComponent = isMobile ? MobileTabs : BaseTabs;

  return (
    <Box
      paddingTop={isMobile ? 0 : 2}
    >
      <TabsComponent
        centered={false}
        tabs={tabs}
        value={tab}
        handleChange={handleChange}
        isHeader
        {...(commissionedSalesPermissions.isAllowManage && {
          secondaryAction: (
            <Stack width="100%">
              <FormProvider {...methods}>
                {tab === '1' && !permissions.isEmployee && (
                <Stack pb={isMobile ? 2 : 0} spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
                  <BaseTooltip tooltipText={t('clockHereToStartAGuided', { ns: 'common' })}>
                    <Box
                      sx={{
                        cursor: 'pointer',
                      }}
                      alignItems="center"
                      display="flex"
                      onClick={handleStartTour}
                    >
                      <Iconify icon="cuida:help-outline" width={24} sx={{ color: '#A855F7' }} />
                    </Box>
                  </BaseTooltip>
                  {allowFeatureAccess('reports') && (
                  <BaseButtonMenu
                    isRemoveBtnHover={false}
                    buttonProps={{
                      children: t('getReport', { ns: 'payroll' }),
                      endIcon: (
                        <Icon path={mdiChevronDown} size={0.8} />
                      ),
                      variant: 'contained',
                      disabled: isOrdersLoading,
                    }}
                    listProps={{
                      items,
                      sx: {
                        width: 'auto',
                      },
                      itemfontsize: 12,
                    }}
                  />
                  )}
                </Stack>
                )}
              </FormProvider>
            </Stack>
          ),
        })}
      >
        <TabPanel
          sx={{
            paddingX: 0,
            pt: isMobile ? 0 : '',
          }}
          value="1"
        >
          <OrdersTotalsHeader />
          <OrdersList columnVisibilityModel={columnVisibilityModel} />
          <OrdersTour runTour={runTour} handleFinishTour={handleFinishTour} />
        </TabPanel>
        <TabPanel
          sx={{
            paddingX: 0,
            pt: isMobile ? 0 : '',
          }}
          value="2"
        >
          <CommissionsTab />
        </TabPanel>
        <TabPanel
          sx={{
            paddingX: 0,
            pt: isMobile ? 0 : '',
          }}
          value="3"
        >
          <TeamsSummary />
        </TabPanel>
        <TabPanel
          sx={{
            paddingX: 0,
            pt: isMobile ? 0 : '',
          }}
          value="4"
        >
          <PromotersSummary />
        </TabPanel>
      </TabsComponent>
      <VideoModal
        isOpenModal={isOpenShopifyIdVideo}
        setIsOpenModal={(val) => dispatch(memberActions.setIsOpenShopifyIdVideo(val))}
        videoTitle={t('linkShopifyStaffToManageMate', { ns: 'common' })}
        videoLink="https://www.youtube.com/watch?v=zX__AWlhDRU"
      />
    </Box>
  );
};

export {
  Orders,
};
